import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import {
  getAdminStats, getAdminEvents, createAdminEvent, updateAdminEvent, deleteAdminEvent,
  getAdminWaitlist, getAdminBookings, getAdminMembers, getCloudinarySignature, bulkUploadMembers
} from '@/lib/api';
import AuthModal from '@/components/AuthModal';
import BunchhLogo from '@/components/BunchhLogo';
import { toast } from 'sonner';
import {
  LayoutDashboard, CalendarDays, ClipboardList, Users, CreditCard,
  Plus, Pencil, Trash2, Upload, X, LogOut, Loader2, Eye, UserPlus
} from 'lucide-react';

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'events', label: 'Events', icon: CalendarDays },
  { id: 'waitlist', label: 'Waitlist', icon: ClipboardList },
  { id: 'bookings', label: 'Bookings', icon: CreditCard },
  { id: 'members', label: 'Members', icon: Users },
];

const EMPTY_EVENT = {
  title: '', description: '', city: '', event_type: '', date: '', time: '',
  price: 0, total_spots: 10, image_url: '', vibe: ''
};

export default function AdminDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [members, setMembers] = useState([]);
  const [showAuth, setShowAuth] = useState(false);
  const [loading, setLoading] = useState(false);
  const [eventModal, setEventModal] = useState(null); // null | 'create' | event object
  const [eventForm, setEventForm] = useState(EMPTY_EVENT);
  const [uploading, setUploading] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [bulkEmails, setBulkEmails] = useState('');
  const [addingMembers, setAddingMembers] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      if (!user) setShowAuth(true);
      else { toast.error('Admin access required'); navigate('/'); }
    }
  }, [authLoading, user, navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'overview') { const r = await getAdminStats(); setStats(r.data); }
      if (tab === 'events') { const r = await getAdminEvents(); setEvents(r.data); }
      if (tab === 'waitlist') { const r = await getAdminWaitlist(); setWaitlist(r.data); }
      if (tab === 'bookings') { const r = await getAdminBookings(); setBookings(r.data); }
      if (tab === 'members') { const r = await getAdminMembers(); setMembers(r.data); }
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => {
    if (user?.role === 'admin') loadData();
  }, [user, loadData]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const sigRes = await getCloudinarySignature('bunchh');
      const sig = sigRes.data;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, {
        method: 'POST', body: formData
      });
      const data = await res.json();
      setEventForm(p => ({ ...p, image_url: data.secure_url }));
      toast.success('Image uploaded!');
    } catch { toast.error('Image upload failed'); }
    finally { setUploading(false); }
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...eventForm, price: parseInt(eventForm.price) || 0, total_spots: parseInt(eventForm.total_spots) || 10 };
      if (typeof eventModal === 'object' && eventModal?.id) {
        await updateAdminEvent(eventModal.id, payload);
        toast.success('Event updated!');
      } else {
        await createAdminEvent(payload);
        toast.success('Event created!');
      }
      setEventModal(null);
      setEventForm(EMPTY_EVENT);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save event'); }
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await deleteAdminEvent(id);
      toast.success('Event deleted');
      loadData();
    } catch { toast.error('Failed to delete'); }
  };

  const openEditEvent = (event) => {
    setEventForm({
      title: event.title || '', description: event.description || '', city: event.city || '',
      event_type: event.event_type || '', date: event.date || '', time: event.time || '',
      price: event.price || 0, total_spots: event.total_spots || 10,
      image_url: event.image_url || '', vibe: event.vibe || ''
    });
    setEventModal(event);
  };

  const handleBulkUpload = async () => {
    const emails = bulkEmails.split(/[\n,;]+/).map(e => e.trim()).filter(e => e);
    if (!emails.length) return toast.error('Enter at least one email');
    setAddingMembers(true);
    try {
      const res = await bulkUploadMembers(emails);
      toast.success(res.data.message);
      setBulkEmails('');
      setShowAddMember(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add members');
    } finally {
      setAddingMembers(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  if (authLoading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}><Loader2 size={32} className="animate-spin" color="var(--coral)" /></div>;
  }

  if (!user && showAuth) return <AuthModal onClose={() => { setShowAuth(false); navigate('/'); }} />;
  if (!user || user.role !== 'admin') return null;

  return (
    <div className="admin-layout" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="admin-sidebar" data-testid="admin-sidebar">
        <div className="admin-sidebar-logo">
          <BunchhLogo height={24} />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Admin Dashboard</div>
        </div>
        {TABS.map(t => (
          <button
            key={t.id}
            className={`admin-nav-item ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
        <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <button className="admin-nav-item" onClick={() => navigate('/members')} data-testid="admin-view-site"><Eye size={16} /> View Site</button>
          <button className="admin-nav-item" onClick={handleLogout} data-testid="admin-logout"><LogOut size={16} /> Logout</button>
        </div>
      </aside>

      {/* Main */}
      <main className="admin-main">
        {/* Overview */}
        {tab === 'overview' && (
          <div data-testid="admin-overview">
            <div className="admin-header">
              <h1 className="admin-title">Dashboard Overview</h1>
              <p className="admin-subtitle">Welcome back, {user.name}</p>
            </div>
            {stats && (
              <div className="stat-grid">
                <div className="stat-box"><div className="stat-box-label">Total Members</div><div className="stat-box-value">{stats.total_members}</div></div>
                <div className="stat-box"><div className="stat-box-label">Waitlist</div><div className="stat-box-value">{stats.total_waitlist}</div></div>
                <div className="stat-box"><div className="stat-box-label">Active Events</div><div className="stat-box-value">{stats.total_events}</div></div>
                <div className="stat-box"><div className="stat-box-label">Total Bookings</div><div className="stat-box-value">{stats.total_bookings}</div></div>
                <div className="stat-box"><div className="stat-box-label">Paid Bookings</div><div className="stat-box-value">{stats.paid_bookings}</div></div>
              </div>
            )}
          </div>
        )}

        {/* Events */}
        {tab === 'events' && (
          <div data-testid="admin-events">
            <div className="admin-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><h1 className="admin-title">Events</h1><p className="admin-subtitle">Manage your events and plans</p></div>
              <button className="btn-coral btn-sm" onClick={() => { setEventForm(EMPTY_EVENT); setEventModal('create'); }} data-testid="admin-create-event-btn" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Plus size={16} /> Add Event
              </button>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead><tr><th>Title</th><th>Type</th><th>City</th><th>Date</th><th>Price</th><th>Spots</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {events.filter(e => e.status === 'active').map(e => (
                    <tr key={e.id}>
                      <td style={{ fontWeight: 600 }}>{e.title}</td>
                      <td>{e.event_type}</td>
                      <td>{e.city}</td>
                      <td>{e.date}</td>
                      <td>{'\u20B9'}{e.price}</td>
                      <td>{e.total_spots}</td>
                      <td><span className="status-badge status-paid">{e.status}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => openEditEvent(e)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--coral)' }} data-testid={`edit-event-${e.id}`}><Pencil size={16} /></button>
                          <button onClick={() => handleDeleteEvent(e.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }} data-testid={`delete-event-${e.id}`}><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {events.filter(e => e.status === 'active').length === 0 && (
                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No events yet. Create your first event!</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Waitlist */}
        {tab === 'waitlist' && (
          <div data-testid="admin-waitlist">
            <div className="admin-header"><h1 className="admin-title">Waitlist Submissions</h1><p className="admin-subtitle">{waitlist.length} applications</p></div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Email</th><th>City</th><th>DOB</th><th>Identity</th><th>Occupation</th><th>WhatsApp</th><th>Status</th><th>Date</th></tr></thead>
                <tbody>
                  {waitlist.map(w => (
                    <tr key={w.session_id}>
                      <td style={{ fontWeight: 600 }}>{w.first_name} {w.last_name}</td>
                      <td>{w.email}</td>
                      <td>{w.city}</td>
                      <td>{w.date_of_birth}</td>
                      <td>{w.part2?.gender_identity || '-'}</td>
                      <td>{w.part2?.occupation || '-'}</td>
                      <td>{w.part2?.whatsapp_number || '-'}</td>
                      <td><span className={`status-badge ${w.part2_completed ? 'status-paid' : 'status-pending'}`}>{w.part2_completed ? 'Complete' : 'Part 1 only'}</span></td>
                      <td>{new Date(w.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {waitlist.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No waitlist submissions yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Bookings */}
        {tab === 'bookings' && (
          <div data-testid="admin-bookings">
            <div className="admin-header"><h1 className="admin-title">Bookings</h1><p className="admin-subtitle">{bookings.length} total bookings</p></div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Email</th><th>Event</th><th>Phone</th><th>Payment</th><th>Date</th></tr></thead>
                <tbody>
                  {bookings.map(b => (
                    <tr key={b.id}>
                      <td style={{ fontWeight: 600 }}>{b.name}</td>
                      <td>{b.email}</td>
                      <td>{b.event_title}</td>
                      <td>{b.phone}</td>
                      <td><span className={`status-badge ${b.payment_status === 'paid' ? 'status-paid' : 'status-pending'}`}>{b.payment_status}</span></td>
                      <td>{new Date(b.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {bookings.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No bookings yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Members */}
        {tab === 'members' && (
          <div data-testid="admin-members">
            <div className="admin-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><h1 className="admin-title">Members</h1><p className="admin-subtitle">{members.length} registered members</p></div>
              <button className="btn-coral btn-sm" onClick={() => setShowAddMember(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }} data-testid="add-member-btn">
                <UserPlus size={16} /> Add Members
              </button>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Email</th><th>City</th><th>Phone</th><th>Paid Bookings</th><th>Joined</th></tr></thead>
                <tbody>
                  {members.map(m => (
                    <tr key={m.id}>
                      <td style={{ fontWeight: 600 }}>{m.name}</td>
                      <td>{m.email}</td>
                      <td>{m.city || '-'}</td>
                      <td>{m.phone || '-'}</td>
                      <td><span className={`status-badge ${m.total_paid_bookings > 0 ? 'status-paid' : 'status-pending'}`}>{m.total_paid_bookings}</span></td>
                      <td>{new Date(m.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {members.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No members yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="admin-mobile-nav">
        {TABS.map(t => (
          <button key={t.id} className={`admin-mobile-nav-item ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            <t.icon size={20} />
            <span>{t.label}</span>
          </button>
        ))}
      </nav>

      {/* Add Members Modal */}
      {showAddMember && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowAddMember(false)}>
          <div className="modal-box" style={{ maxWidth: 480 }}>
            <button className="modal-close" onClick={() => setShowAddMember(false)}><X size={20} /></button>
            <div className="modal-eyebrow">Approve Access</div>
            <h2 className="modal-title">Add Members</h2>
            <p className="modal-sub">Paste email addresses below — one per line, or separated by commas. These people will be able to log in immediately.</p>
            <div className="form-group">
              <label className="form-label">Email addresses</label>
              <textarea
                className="form-input"
                style={{ minHeight: 140, resize: 'vertical' }}
                placeholder={"name@email.com\nanother@email.com"}
                value={bulkEmails}
                onChange={e => setBulkEmails(e.target.value)}
                data-testid="bulk-email-input"
              />
            </div>
            <button className="btn-coral" onClick={handleBulkUpload} disabled={addingMembers} style={{ width: '100%' }} data-testid="bulk-upload-btn">
              {addingMembers ? <Loader2 size={18} className="animate-spin" style={{ display: 'inline' }} /> : `Approve ${bulkEmails.split(/[\n,;]+/).filter(e => e.trim()).length || 0} member(s)`}
            </button>
          </div>
        </div>
      )}

      {/* Event Modal */}
      {eventModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setEventModal(null)} data-testid="event-form-modal">
          <div className="modal-box" style={{ maxWidth: 560 }}>
            <button className="modal-close" onClick={() => setEventModal(null)}><X size={20} /></button>
            <div className="modal-eyebrow">{typeof eventModal === 'object' ? 'Edit Event' : 'Create Event'}</div>
            <h2 className="modal-title">{typeof eventModal === 'object' ? 'Update Event Details' : 'Add New Event'}</h2>
            <form onSubmit={handleSaveEvent}>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input className="form-input" value={eventForm.title} onChange={e => setEventForm(p => ({ ...p, title: e.target.value }))} required data-testid="event-title-input" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Event Type</label>
                  <input className="form-input" placeholder="e.g. Art Evening" value={eventForm.event_type} onChange={e => setEventForm(p => ({ ...p, event_type: e.target.value }))} required data-testid="event-type-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">City</label>
                  <select className="form-select" value={eventForm.city} onChange={e => setEventForm(p => ({ ...p, city: e.target.value }))} required data-testid="event-city-input">
                    <option value="">Select</option>
                    {['Mumbai', 'Delhi', 'Bangalore', 'Pune'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Vibe / Description</label>
                <input className="form-input" placeholder="One-liner vibe" value={eventForm.vibe} onChange={e => setEventForm(p => ({ ...p, vibe: e.target.value }))} data-testid="event-vibe-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Full Description</label>
                <textarea className="form-input" style={{ minHeight: 80 }} value={eventForm.description} onChange={e => setEventForm(p => ({ ...p, description: e.target.value }))} required data-testid="event-desc-input" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input className="form-input" placeholder="e.g. Sat, Apr 12" value={eventForm.date} onChange={e => setEventForm(p => ({ ...p, date: e.target.value }))} required data-testid="event-date-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Time</label>
                  <input className="form-input" placeholder="e.g. 6:30 PM" value={eventForm.time} onChange={e => setEventForm(p => ({ ...p, time: e.target.value }))} required data-testid="event-time-input" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Price ({'\u20B9'})</label>
                  <input className="form-input" type="number" value={eventForm.price} onChange={e => setEventForm(p => ({ ...p, price: e.target.value }))} required data-testid="event-price-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Total Spots</label>
                  <input className="form-input" type="number" value={eventForm.total_spots} onChange={e => setEventForm(p => ({ ...p, total_spots: e.target.value }))} required data-testid="event-spots-input" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Event Image</label>
                {eventForm.image_url && (
                  <div style={{ marginBottom: 8, borderRadius: 12, overflow: 'hidden', height: 120 }}>
                    <img src={eventForm.image_url} alt="Event" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', border: '1.5px dashed var(--border)', borderRadius: 12, cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14 }}>
                  <Upload size={16} />
                  {uploading ? 'Uploading...' : 'Upload image'}
                  <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} data-testid="event-image-upload" />
                </label>
              </div>
              <button className="btn-coral" type="submit" style={{ width: '100%', marginTop: 8 }} data-testid="event-save-btn">
                {typeof eventModal === 'object' ? 'Update Event' : 'Create Event'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
